import { useEffect, useMemo, useRef, useState } from "react";
import {
  checkOutlookStatus,
  connectOutlook,
  deleteTeamsEvent,
  generateTeamsLink,
  logoutOutlook,
} from "../../Pages/Calendar/outlookClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTimes,
  faExclamationTriangle,
  faEllipsisVertical,
  faVideo,
  faLocationDot,
  faLink,
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlass,
  faCalendarDays,
  faClock,
  faCopy,
  faUsers,
  faCircleInfo,
  faCircle,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import { Button, Col, Form, Row, Modal, Dropdown } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  DeleteRequest,
  GetRequest,
  PostRequest,
  PutRequest,
} from "../../Utils/hooks/ApiHandler";
import { useGetEssentials } from "../../Utils/hooks/useGetEssentials";
import Select from "react-select";
import { useLocation } from "react-router-dom";
// timezone lib
import TimezoneSelect, { allTimezones } from "react-timezone-select";
import { useGetRequest } from "../../Utils/hooks/QueryHandler";
import DeleteModal from "../DeleteModal";
import {
  customSelectStyles,
  customStyles,
  formatDate,
  formatTime,
} from "../../Utils/helpers/commonhelper";
import { IconPoint } from "@tabler/icons-react";
import { UnauthViewModal } from "../../CommonComponents/UnauthViewModal";
import { useOutsideClickHandler } from "../../Utils/hooks/useOutsideClickHandler";
import useCommonData from "../../Utils/actions/advanceFilteringAction";
import { useGoogleLogin } from "@react-oauth/google";
import {
  createGoogleMeetEvent,
  patchEventGapi,
  deleteEventGapi,
  initGapiClient,
  setGapiAccessToken,
} from "../Calendar/googleClient";

const optionalUrl = (label) =>
  z
    .string()
    .trim()
    .optional()
    .nullable()
    .refine(
      (val) => {
        if (!val || val === "") return true;
        if (val.includes(" ")) return false;
        return /^https?:\/\/.+/.test(val);
      },
      { message: `Invalid ${label} URL` },
    );

const meetingSchema = z
  .object({
    assign_to: z.string().optional(),
    meeting_title: z.string().min(1, "Meeting Title is Required"),
    meeting_date: z.string().min(1, "Meeting date is required"),
    meeting_to: z.string(),
    meeting_from: z.string(),
    meeting_type: z.string(),
    meeting_link: optionalUrl("Meeting link"),
    address: z.string().optional(),
    description: z.string().optional(),
    meeting_status: z.string(),
  })
  .refine(
    (data) => {
      if (data.meeting_type === "virtual") {
        data.address = "";
        return data.meeting_link && data.meeting_link.trim() !== "";
      }
      return true;
    },
    {
      message: "Meeting link is required for virtual meetings",
      path: ["meeting_link"],
    },
  )
  .refine(
    (data) => {
      if (data.meeting_type !== "virtual") {
        data.meeting_link = "";
        return data.address && data.address.trim() !== "";
      }
      return true;
    },
    {
      message: "Address is required for non-virtual meetings",
      path: ["address"],
    },
  );

const formatDateForAPI = (date) => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const cleanTimeFormat = (timeString) => {
  if (!timeString) return "";
  return timeString.replace(/ (AM|PM)$/i, "");
};

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const dayShortNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const isSameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const defaultTimezone = {
  value: "Asia/Kolkata",
  label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
  offset: 5.5,
  abbrev: "IST",
  altName: "India Standard Time",
};


export const ProjectMeeting = ({
  projectId,
  leadId,
  email,
  fullName,
  module_name = "projects",
  meetingGrant,
  isReadOnly = false,
}) => {
  const recordId = projectId ?? leadId;

  const { headers, extras, navigate, customerId, loginUserId } = useGetEssentials();
  const [isOutlookLoggedIn, setIsOutlookLoggedIn] = useState(
    () => localStorage.getItem("outlook_connected") === "true",
  );
  const [isGeneratingOutlookLink, setIsGeneratingOutlookLink] = useState(false);
  const [draftTeamsEventId, setDraftTeamsEventId] = useState(null);
  const [meetings, setMeetings] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editMeetingForm, setEditMeetingForm] = useState(false);
  const [editMeetingData, setEditMeetingData] = useState(null);
  const [isPosting, setIsPosting] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [meetingFilter, setMeetingFilter] = useState("today"); // "today" (selected day) | "all"
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [confirmDeleteMeeting, setConfirmDeleteMeeting] = useState(false);
  const [deleteMeetingId, setDeleteMeetingId] = useState(null);

  const emailDropdownRef = useRef(null);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [currentEmailInput, setCurrentEmailInput] = useState("");
  const [showEmailDropdown, setShowEmailDropdown] = useState(false);
  const [emailOptions, setEmailOptions] = useState(null);
  const [isLoadingEmails, setIsLoadingEmails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailSearchInput, setEmailSearchInput] = useState("");
  const [debounceEmailSearchInput, setDebounceEmailSearchInput] = useState("");

  const [hasConflict, setHasConflict] = useState(false);
  const [hasShownWarning, setHasShownWarning] = useState(false);
  const [expandedTitles, setExpandedTitles] = useState({});
  const [warningMessage, setWarningMessage] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState(defaultTimezone);

  // ---- calendar (month grid) state ----
  const [viewMonth, setViewMonth] = useState(new Date());
  const [selectedDateForApi, setSelectedDateForApi] = useState(new Date());

  const [isGoogleLoggedIn, setIsGoogleLoggedIn] = useState(false);
  const [isGeneratingMeetLink, setIsGeneratingMeetLink] = useState(false);
  const [draftMeetEventId, setDraftMeetEventId] = useState(null);
  const draftMeetEventIdRef = useRef(null);

  useEffect(() => {
    draftMeetEventIdRef.current = draftMeetEventId;
  }, [draftMeetEventId]);

  useEffect(() => {
    return () => {
      if (draftMeetEventIdRef.current) {
        deleteEventGapi(draftMeetEventIdRef.current).catch(() => { });
      }
    };
  }, []);

  const draftTeamsEventIdRef = useRef(null);

  useEffect(() => {
    draftTeamsEventIdRef.current = draftTeamsEventId;
  }, [draftTeamsEventId]);

  useEffect(() => {
    return () => {
      if (draftTeamsEventIdRef.current) {
        deleteTeamsEvent(
          draftTeamsEventIdRef.current,
          loginUserId,
        );
      }
    };
  }, []);

  const meetingTypeOptions = [
    { value: "inperson", label: "In-Person" },
    { value: "virtual", label: "Virtual" },
  ];

  const meetingStatusOptions = [
    { value: "scheduled", label: "Scheduled" },
    { value: "conducted", label: "Conducted" },
    { value: "missed", label: "Missed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const statusFilterOptions = [
    { value: "all", label: "All Status" },
    { value: "scheduled", label: "Scheduled" },
    { value: "conducted", label: "Conducted" },
    { value: "missed", label: "Missed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const { leadOwner } = useCommonData({
    leadOwner: true,
    leadSource: false,
    companies: false,
    contacts: false,
  });
  const [isInfoHovered, setInfoHovered] = useState(false);
  const meetingLinkDetails = [
    {
      description:
        "This is the event link for the Teams meeting. Due to account restrictions, a direct join link may not be available. You can also add the actual Teams meeting join URL here before submitting the meeting. Simply open the meeting in Microsoft Teams, copy the join URL, and paste it into the Meeting Link field above.",
    },
  ];
  const [teamsMeetingType, setTeamsMeetingType] = useState("");

  const getCurrentTime = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 5);
  };

  const addThirtyMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    date.setMinutes(date.getMinutes() + 30);
    return date.toTimeString().slice(0, 5);
  };

  const addEmailTag = (emailToAdd = null) => {
    const emailValue = emailToAdd || currentEmailInput.trim();
    if (emailValue && !emailRecipients.includes(emailValue)) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(emailValue)) {
        setEmailRecipients((prev) => [...prev, emailValue]);
        setCurrentEmailInput("");
        setShowEmailDropdown(false);
      } else {
        toast.error("Please enter a valid email address");
      }
    }
  };

  const removeEmailTag = (emailToRemove) => {
    if (emailToRemove === email) return;
    setEmailRecipients((prev) => prev.filter((e) => e !== emailToRemove));
    setDebounceEmailSearchInput("");
  };

  const handleEmailInputChange = (e) => {
    const value = e.target.value;
    setCurrentEmailInput(value);
    setEmailSearchInput(value);
    setShowEmailDropdown(value.length > 0);
  };

  const handleEmailInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addEmailTag();
    } else if (
      e.key === "Backspace" &&
      currentEmailInput === "" &&
      emailRecipients.length > 0
    ) {
      removeEmailTag(emailRecipients[emailRecipients.length - 1]);
    }
  };

  const setCurrentTimeValues = () => {
    const currentTime = getCurrentTime();
    const endTime = addThirtyMinutes(currentTime);
    setValue("meeting_from", currentTime);
    setValue("meeting_to", endTime);
  };

  const today = new Date().toISOString().split("T")[0];

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
    setValue,
  } = useForm({
    resolver: zodResolver(meetingSchema),
    defaultValues: {
      meeting_title: "",
      meeting_date: today,
      meeting_to: addThirtyMinutes(getCurrentTime()),
      meeting_from: getCurrentTime(),
      address: "",
      assign_to: loginUserId,
      meeting_type: "inperson",
      meeting_link: "",
      description: "",
      meeting_status: "scheduled",
    },
  });

  const toggleTitle = (id) => {
    setExpandedTitles((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const truncate = (text, limit = 60) =>
    text && text.length > limit ? text.substring(0, limit) + "…" : text;

  useEffect(() => {
    if (email) setEmailRecipients([email]);
  }, [email]);

  useOutsideClickHandler({
    objRef: emailDropdownRef,
    isDropdown: showEmailDropdown,
    setDropdown: setShowEmailDropdown,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounceEmailSearchInput(emailSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [emailSearchInput]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoadingEmails(true);
        const res = await GetRequest(
          `lead/getAllEmailWithNameForMeeting/${customerId}?search=${debounceEmailSearchInput}`,
          { headers },
          extras,
        );
        if (res.status === "fail" || res.success === false) {
          toast.error(res.message);
          return;
        }
        if (res) setEmailOptions(res);
      } catch (error) {
        // silent
      } finally {
        setIsLoadingEmails(false);
      }
    })();
  }, [debounceEmailSearchInput]);

  const meetingType = watch("meeting_type");

  useEffect(() => {
    if (editMeetingData && editMeetingForm) {
      reset({
        meeting_title: editMeetingData.meeting_title || "",
        meeting_date: editMeetingData.meeting_date || "",
        meeting_to:
          cleanTimeFormat(editMeetingData.meeting_to) ||
          addThirtyMinutes(getCurrentTime()),
        meeting_from:
          cleanTimeFormat(editMeetingData.meeting_from) || getCurrentTime(),
        address: editMeetingData.address || "",
        meeting_type: editMeetingData.meeting_type || "inperson",
        meeting_link: editMeetingData.meeting_link || "",
        description: editMeetingData.description || "",
        meeting_status: editMeetingData.meeting_status || "scheduled",
        assign_to: editMeetingData.assign_to || loginUserId,
      });

      if (editMeetingData.timezone) {
        setSelectedTimezone(JSON.parse(editMeetingData.timezone));
      }
      if (editMeetingData.invitees) {
        const parsedMails = JSON.parse(editMeetingData.invitees);
        setEmailRecipients(parsedMails.map((invitee) => invitee.email));
      }
    }
  }, [editMeetingData]);

  const formattedDate =
    meetingFilter === "today" ? formatDateForAPI(selectedDateForApi) : "";

  const {
    data,
    error,
    refetch: meetingRefetch,
    isLoading: meetingIsLoading,
  } = useGetRequest(
    `/lead/fetchMeetings/${recordId}/${customerId}/${module_name}/${loginUserId}${formattedDate !== "" ? `?date=${formattedDate}` : ""
    }`,
    ["projectMeetingsQuery", selectedDateForApi, formattedDate],
    headers,
    extras,
  );

  useEffect(() => {
    if (data && data.data) setMeetings(data.data);
  }, [data]);

  const handleWarningCheck = async ({
    action,
    meet_id,
    assign_to_id,
    date,
    time,
  }) => {
    const payload = {
      action,
      meeting_id: meet_id || null,
      customer_id: customerId,
      assign_to: assign_to_id,
      meeting_date: date,
      meeting_from: time,
    };
    try {
      const warningRes = await PostRequest(
        "lead/checkAlreadyMeeting_schedule",
        payload,
        false,
        { headers },
        extras,
      );
      if (warningRes.status === "fail") return false;
      setWarningMessage(warningRes.message);
      return true;
    } catch (error) {
      toast.error("Error checking schedule!");
      return false;
    }
  };

  const invitees =
    emailOptions !== null
      ? emailRecipients
        .map((recipientEmail) => {
          for (const [module, list] of Object.entries(emailOptions)) {
            if (recipientEmail === email) {
              return { email, fullName, id: recordId, module: "contacts" };
            } else {
              const found = list.find(
                (item) => item.email === recipientEmail,
              );
              if (found) return { ...found, module };
              return {
                email: recipientEmail,
                fullName: recipientEmail?.split("@")[0],
                id: 0,
                module: "guest",
              };
            }
          }
          return null;
        })
        .filter(Boolean)
      : [];

  const resetFormState = () => {
    reset({
      meeting_title: "",
      meeting_date: today,
      meeting_to: addThirtyMinutes(getCurrentTime()),
      meeting_from: getCurrentTime(),
      address: "",
      meeting_type: "inperson",
      meeting_link: "",
      description: "",
      meeting_status: "scheduled",
      assign_to: loginUserId,
    });
    setEmailRecipients(email ? [email] : []);
    setSelectedTimezone(defaultTimezone);
    setTeamsMeetingType('');
  };

  const onSubmit = async (data) => {
    if (!meetingGrant?.isCreate) {
      toast.warning("You are not authorized for this action!");
      return;
    }
    const meeting_payload = {
      module_id: recordId,
      module_name,
      customer_id: customerId,
      ...data,
      invitees,
      timezone: selectedTimezone,
      user_login_id: loginUserId,
    };

    const conflictExists = await handleWarningCheck({
      action: "create",
      meet_id: null,
      assign_to_id: data.assign_to,
      date: data.meeting_date,
      time: data.meeting_from,
    });

    if (conflictExists && !hasShownWarning) {
      setHasConflict(true);
      setHasShownWarning(true);
      return;
    } else {
      setHasConflict(false);
    }

    setIsPosting(true);
    try {
      const res = await PostRequest(
        `lead/createMeeting`,
        meeting_payload,
        false,
        { headers },
        extras,
      );

      if (res.status === "fail" || res.success === false) {
        toast.error(res.message);
        return;
      }

      if (draftMeetEventId) {
        try {
          await patchEventGapi(draftMeetEventId, {
            summary: data.meeting_title,
            start: {
              dateTime: new Date(
                `${data.meeting_date}T${data.meeting_from}`,
              ).toISOString(),
            },
            end: {
              dateTime: new Date(
                `${data.meeting_date}T${data.meeting_to}`,
              ).toISOString(),
            },
          });
        } catch (e) {
          // non-blocking
        }
        setDraftMeetEventId(null);
      }

      if (draftTeamsEventId) {
        setDraftTeamsEventId(null);
      }

      resetFormState();
      meetingRefetch();
      setShowAddForm(false);
      setEditMeetingForm(false);
      setEditMeetingData(null);
      setHasShownWarning(false);
      setHasConflict(false);
      toast.success(res.message);
    } catch (e) {
      toast.error("Please Try Re-Scheduling");
    } finally {
      setIsPosting(false);
    }
  };

  const handleUpdateMeeting = async (data) => {
    const meeting_payload = {
      module_id: recordId,
      meeting_id: editMeetingData.id,
      module_name,
      ...data,
      customer_id: customerId,
      invitees,
      timezone: selectedTimezone,
      user_login_id: loginUserId,
    };

    const conflictExists = await handleWarningCheck({
      action: "create",
      meet_id: null,
      assign_to_id: data.assign_to,
      date: data.meeting_date,
      time: data.meeting_from,
    });

    if (conflictExists && !hasShownWarning) {
      setHasConflict(true);
      setHasShownWarning(true);
      return;
    } else {
      setHasConflict(false);
    }

    setIsPosting(true);
    try {
      const res = await PutRequest(
        `lead/updateMeeting/${editMeetingData.id}`,
        meeting_payload,
        { headers },
        extras,
      );

      if (res.status === "fail" || res.success === false) {
        toast.error(res.message);
        return;
      }

      if (draftMeetEventId) {
        try {
          await patchEventGapi(draftMeetEventId, {
            summary: data.meeting_title,
            start: {
              dateTime: new Date(
                `${data.meeting_date}T${data.meeting_from}`,
              ).toISOString(),
            },
            end: {
              dateTime: new Date(
                `${data.meeting_date}T${data.meeting_to}`,
              ).toISOString(),
            },
          });
        } catch (e) {
          // non-blocking
        }
        setDraftMeetEventId(null);
      }

      if (draftTeamsEventId) {
        setDraftTeamsEventId(null);
      }

      resetFormState();
      meetingRefetch();
      setEditMeetingForm(false);
      setEditMeetingData(null);
      setHasShownWarning(false);
      toast.success(res.message);
    } catch (error) {
      toast.error("Update Failed. Please Try Again!");
    } finally {
      setIsPosting(false);
    }
  };

  const handleCancelMeeting = async (meetingId) => {
    try {
      setIsCancelled(true);
      if (!meetingId) return;
      const payload = {
        meeting_id: meetingId,
        user_login_id: loginUserId,
        module_name,
        customer_id: customerId,
      };
      const res = await PutRequest(
        "lead/cancelMeeting",
        payload,
        { headers },
        extras,
      );
      if (res.status === "fail" || res.success === false) {
        toast.error(res.message);
        return;
      }
      meetingRefetch();
      setIsCancelled(false);
      toast.success(res.message);
    } catch (error) {
      toast.error("Failed!");
    }
  };

  const handleEditMeeting = async (meetingId, module_id) => {
    if (!meetingGrant?.isEdit) {
      toast.warning("You are not authorized for this action!");
      return;
    }
    try {
      const res = await GetRequest(
        `lead/getMeetingDetailsById/${meetingId}/${module_id}/${customerId}/${loginUserId}`,
        { headers },
        extras,
      );
      if (res.status === "fail" || res.success === false) {
        toast.error(res.message);
        return;
      }
      setEditMeetingData(res.data?.[0]);
      setEditMeetingForm(true);
      setShowAddForm(false);
    } catch (error) {
      toast.error("Failed to fetch meeting details");
    }
  };

  const handleDeleteMeeting = async () => {
    setLoading(true);
    try {
      if (deleteMeetingId === null) return;
      const delRes = await DeleteRequest(
        `lead/deleteMeeting/${deleteMeetingId}`,
        { headers },
        extras,
      );
      if (delRes.status === "fail" || delRes.success === false) {
        setLoading(false);
        toast.error(delRes.message);
        return;
      }
      setLoading(false);
      toast.success(delRes.message);
      setConfirmDeleteMeeting(false);
      setDeleteMeetingId(null);
      meetingRefetch();
    } catch (error) {
      setLoading(false);
      toast.error("Failed to delete meeting");
    }
  };

  useEffect(() => {
    if (!showAddForm && !editMeetingForm) {
      setHasConflict(false);
      setHasShownWarning(false);
    }
  }, [showAddForm, editMeetingForm]);

  const getMeetingStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "cancelled":
        return { bg: "#e5484d", fg: "#ffffff", dot: "#ffffff" };
      case "missed":
        return { bg: "#f5a524", fg: "#ffffff", dot: "#ffffff" };
      case "scheduled":
        return { bg: "#3b6fe0", fg: "#ffffff", dot: "#ffffff" };
      case "conducted":
        return { bg: "#17c185", fg: "#ffffff", dot: "#ffffff" };
      default:
        return { bg: "#8a8fa3", fg: "#ffffff", dot: "#ffffff" };
    }
  };

  const getMeetingTypeStyle = (type) => {
    switch (type?.toLowerCase()) {
      case "virtual":
        return { bg: "#3b6fe0", fg: "#ffffff" };
      case "inperson":
      default:
        return { bg: "var(--pmc-primary)", fg: "#ffffff" };
    }
  };

  const handleCloseModal = () => {
    if (draftMeetEventId) {
      deleteEventGapi(draftMeetEventId).catch(() => { });
      setDraftMeetEventId(null);
    }
    if (draftTeamsEventId) {
      deleteTeamsEvent(draftTeamsEventId, loginUserId);
      setDraftTeamsEventId(null);
    }
    setShowAddForm(false);
    setEditMeetingForm(false);
    setEditMeetingData(null);
    resetFormState();
  };

  const openCreateModal = () => {
    if (!meetingGrant?.isCreate) {
      toast.warning("You are not authorized for this action!");
      return;
    }
    if (isReadOnly) return;
    reset();
    setShowAddForm(true);
    setEditMeetingForm(false);
    setEditMeetingData(null);
    setCurrentTimeValues();
  };

  useEffect(() => {
    const token = localStorage.getItem("google_access_token");
    const expiry = localStorage.getItem("google_access_token_expires_at");
    if (token && expiry && Number(expiry) > Date.now()) {
      setIsGoogleLoggedIn(true);
      initGapiClient().then(() => setGapiAccessToken(token));
    }
  }, []);

  const googleLogin = useGoogleLogin({
    scope:
      "https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events",
    onSuccess: async ({ access_token, expires_in }) => {
      localStorage.setItem("google_access_token", access_token);
      localStorage.setItem(
        "google_access_token_expires_at",
        String(Date.now() + expires_in * 1000),
      );
      await initGapiClient();
      setGapiAccessToken(access_token);
      setIsGoogleLoggedIn(true);
      toast.success("Google account connected!");
    },
    onError: () => toast.error("Google login failed"),
  });

  const handleOutlookLogin = () => {
    const userId = loginUserId;
    if (!userId) {
      toast.error("Can't connect Outlook - user not identified.");
      return;
    }
    connectOutlook(userId);
  };
  // Google Logout Handler - clears stored token + local flag
  const handleGoogleLogout = () => {
    localStorage.removeItem("google_access_token");
    localStorage.removeItem("google_access_token_expires_at");
    setIsGoogleLoggedIn(false);
    toast.success("Google disconnected");
  };

  const handleOutlookLogout = async () => {
    const userId = loginUserId;
    await logoutOutlook(userId);
    localStorage.removeItem("outlook_connected");
    setIsOutlookLoggedIn(false);
    toast.success("Outlook disconnected");
  };

  const location = useLocation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    if (queryParams.get("connected") === "true") {
      setIsOutlookLoggedIn(true);
      localStorage.setItem("outlook_connected", "true");
      navigate(location.pathname, { replace: true });
    } else {
      const isSaved = localStorage.getItem("outlook_connected");
      if (isSaved === "true") {
        setIsOutlookLoggedIn(true);
      }
    }
  }, [location, navigate]);
  useEffect(() => {
    const userId = loginUserId;
    if (!userId) return;
    checkOutlookStatus(userId).then((connected) => {
      setIsOutlookLoggedIn(connected);
      if (connected) {
        localStorage.setItem("outlook_connected", "true");
      } else {
        localStorage.removeItem("outlook_connected");
      }
    });
  }, [loginUserId]);
  const handleGenerateTeamsLink = async () => {
    try {
      setIsGeneratingOutlookLink(true);
      const meetDate = watch("meeting_date");
      const meetFrom = watch("meeting_from");
      const meetTo = watch("meeting_to");
      const title = watch("meeting_title");
      const description = watch("description");
      const invitees = watch("invitees") || [];

      if (!meetDate || !meetFrom || !meetTo || !title) {
        return toast.error("Please provide meeting title & time");
      }

      const startISO = `${meetDate}T${meetFrom}:00`;
      const endISO = `${meetDate}T${meetTo}:00`;

      const userId = loginUserId;
      if (!userId) {
        toast.error("Can't generate Teams link - user not identified.");
        return;
      }
      const { meetLink: teamsLink, eventId , meeting_type } = await generateTeamsLink({
        userId,
        title,
        description,
        startISO,
        endISO,
        invitees,
      });

      if (teamsLink) {
        setValue("meeting_link", teamsLink, { shouldValidate: true });
        setTeamsMeetingType(meeting_type)
        localStorage.setItem("outlook_connected", "true");
        setIsOutlookLoggedIn(true);
        if (eventId) {
          setDraftTeamsEventId(eventId);
        }
        toast.success("Teams meeting link generated!");
        if (isGoogleLoggedIn) {
        }
      } else {
        toast.error("Failed to generate Teams link");
      }
    } catch (err) {
      if (err?.code === 401) {
        localStorage.removeItem("outlook_connected");
        setIsOutlookLoggedIn(false);
        toast.error("Outlook session expired. Please connect again.");
      } else {
        toast.error("Error connecting to backend server");
      }
    } finally {
      setIsGeneratingOutlookLink(false);
    }
  };
  // ---------------- month grid helpers ----------------
  const buildMonthGrid = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) {
      const d = new Date(year, month, i - startOffset + 1);
      cells.push({ date: d, inMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ date: new Date(year, month, d), inMonth: true });
    }
    while (cells.length % 7 !== 0 || cells.length < 42) {
      const last = cells[cells.length - 1].date;
      const next = new Date(last);
      next.setDate(next.getDate() + 1);
      cells.push({ date: next, inMonth: false });
      if (cells.length >= 42) break;
    }
    return cells;
  };

  const monthCells = buildMonthGrid(viewMonth);
  const now = new Date();

  const goToPrevMonth = () =>
    setViewMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
    );
  const goToNextMonth = () =>
    setViewMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
    );

  const selectDay = (date) => {
    setMeetingFilter("today");
    setSelectedDateForApi(date);
  };

  const jumpToToday = () => {
    setViewMonth(new Date());
    setMeetingFilter("today");
    setSelectedDateForApi(new Date());
  };

  // ---------------- derived list (client-side search / status filter) ----------------
  const visibleMeetings = useMemo(() => {
    return (meetings || []).filter((m) => {
      const matchesSearch =
        !searchTerm ||
        m.meeting_title?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "all" ||
        m.meeting_status?.toLowerCase() === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [meetings, searchTerm, statusFilter]);

  // keep a valid meeting selected for the details panel whenever the list changes
  useEffect(() => {
    if (!visibleMeetings || visibleMeetings.length === 0) {
      if (selectedMeetingId !== null) setSelectedMeetingId(null);
      return;
    }
    if (!visibleMeetings.some((m) => m.id === selectedMeetingId)) {
      setSelectedMeetingId(visibleMeetings[0].id);
    }
  }, [visibleMeetings]);

  const selectedMeeting =
    visibleMeetings.find((m) => m.id === selectedMeetingId) || null;
  const selectedInvitees = selectedMeeting?.invitees
    ? JSON.parse(selectedMeeting.invitees)
    : [];
  const showJoinButton =
    !!selectedMeeting &&
    selectedMeeting.meeting_type === "virtual" &&
    !!selectedMeeting.meeting_link;

  const initialsOf = (name) =>
    name
      ?.split(" ")
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase();

  // ---------------- week strip (7-day picker used above "Meeting History") ----------------
  const buildWeekStrip = (anchor) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(anchor);
      d.setDate(anchor.getDate() + i);
      days.push(d);
    }
    return days;
  };
  const weekDays = buildWeekStrip(viewMonth);
  const goToPrevWeek = () =>
    setViewMonth((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d;
    });
  const goToNextWeek = () =>
    setViewMonth((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });

  // quick inline status change from the details panel (same behaviour as the Leads meeting details widget)
  const handleQuickStatusChange = async (newStatus) => {
    if (!selectedMeeting) return;
    if (!meetingGrant?.isEdit) {
      toast.warning("You are not authorized for this action!");
      return;
    }
    try {
      setIsUpdatingStatus(true);
      const payload = {
        customer_id: customerId,
        meeting_id: selectedMeeting.id,
        user_login_id: loginUserId,
        module_name: selectedMeeting.module_name || module_name,
        module_id: selectedMeeting.module_id || recordId,
        meeting_status: newStatus,
      };
      const res = await PutRequest(
        `taskboard/updateMeetingStatus`,
        payload,
        { headers },
        extras,
      );
      if (res.status === "fail" || res.success === false) {
        toast.error(res.message);
        return;
      }
      meetingRefetch();
      toast.success(res.message);
    } catch (error) {
      toast.error("Failed to update meeting status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const selectedDayLabel =
    meetingFilter === "today"
      ? isSameDay(selectedDateForApi, now)
        ? "Today"
        : selectedDateForApi.toLocaleDateString(undefined, {
          weekday: "long",
          month: "short",
          day: "numeric",
        })
      : "All Meetings";

  return (
    <div className="pmc-page">
      <style>{`
        .pmc-page{ --pmc-primary:#7a3a5e; --pmc-primary-soft:rgba(122,58,94,0.08); --pmc-border:#e7e8f1; --pmc-bg:#f7f8fc; --pmc-text:#20223a; --pmc-muted:#767892; background:var(--pmc-bg); border-radius:12px; height:calc(100vh - 6rem); max-height:calc(100vh - 6rem); display:flex; flex-direction:column; font-family:inherit; overflow:hidden; }
        .pmc-topbar{ display:flex; align-items:center; justify-content:space-between; gap:16px; padding:20px 24px 14px; flex-wrap:wrap; }
        .pmc-title{ font-size:22px; font-weight:600; color:var(--pmc-text); margin:0; }
        .pmc-subtitle{ font-size:13px; color:var(--pmc-muted); margin-top:2px; }
        .pmc-topbar-actions{ display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
        .pmc-search{ position:relative; }
        .pmc-search input{ border:1px solid var(--pmc-border); border-radius:8px; padding:8px 12px 8px 34px; font-size:13px; min-width:220px; background:#fff; }
        .pmc-search input:focus{ outline:none; border-color:var(--pmc-primary); box-shadow:0 0 0 3px var(--pmc-primary-soft); }
        .pmc-search svg{ position:absolute; left:11px; top:50%; transform:translateY(-50%); color:var(--pmc-muted); font-size:13px; }
        .pmc-select-sm .react-select__control{ min-height:36px; border-radius:8px; border-color:var(--pmc-border); font-size:13px; }
        .pmc-btn-primary{ background:var(--pmc-primary); border:none; color:#fff; font-size:13px; font-weight:500; height:36px; padding:0 16px; border-radius:8px; display:flex; align-items:center; gap:8px; white-space:nowrap; }
        .pmc-btn-primary:hover{ background:#4338ca; color:#fff; }
        .pmc-btn-primary:disabled{ opacity:.55; }
        .pmc-body{ display:flex; flex:1; height:100%; gap:7px; overflow:hidden; }
        //padding:0 24px 24px;
        .pmc-sidebar{ width:290px; flex:none; display:flex; flex-direction:column; gap:16px; }
        .pmc-card{ background:#fff; border:1px solid var(--pmc-border); border-radius:12px; padding:16px; }
        .pmc-cal-head{ display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
        .pmc-cal-month{ font-size:14px; font-weight:600; color:var(--pmc-text); }
        .pmc-cal-nav{ display:flex; gap:4px; }
        .pmc-cal-nav button{ width:26px; height:26px; border-radius:6px; border:1px solid var(--pmc-border); background:#fff; display:flex; align-items:center; justify-content:center; color:var(--pmc-muted); font-size:11px; }
        .pmc-cal-nav button:hover{ background:var(--pmc-primary-soft); color:var(--pmc-primary); }
        .pmc-cal-grid{ display:grid; grid-template-columns:repeat(7,1fr); gap:2px; text-align:center; }
        .pmc-cal-dow{ font-size:10.5px; color:var(--pmc-muted); font-weight:600; padding:4px 0 6px; }
        .pmc-cal-day{ font-size:12.5px; border:none; background:transparent; border-radius:8px; height:30px; display:flex; align-items:center; justify-content:center; color:var(--pmc-text); position:relative; }
        .pmc-cal-day.out{ color:#c7c8d6; }
        .pmc-cal-day:hover{ background:var(--pmc-primary-soft); }
        .pmc-cal-day.today::after{ content:""; position:absolute; bottom:3px; width:4px; height:4px; border-radius:50%; background:var(--pmc-primary); }
        .pmc-cal-day.selected{ background:var(--pmc-primary); color:#fff; font-weight:600; }
        .pmc-cal-day.selected::after{ background:#fff; }
        .pmc-today-link{ font-size:12px; color:var(--pmc-primary); font-weight:500; cursor:pointer; margin-top:8px; display:inline-block; }
        .pmc-filter-group{ display:flex; flex-direction:column; gap:6px; }
        .pmc-filter-title{ font-size:11.5px; text-transform:uppercase; letter-spacing:.04em; color:var(--pmc-muted); font-weight:700; margin-bottom:4px; }
        .pmc-chip{ display:flex; align-items:center; justify-content:space-between; padding:8px 10px; border-radius:8px; font-size:13px; cursor:pointer; color:var(--pmc-text); }
        .pmc-chip:hover{ background:var(--pmc-bg); }
        .pmc-chip.active{ background:var(--pmc-primary-soft); color:var(--pmc-primary); font-weight:600; }
        .pmc-chip .pmc-dot{ width:8px; height:8px; border-radius:50%; margin-right:8px; display:inline-block; }
        .pmc-google-card{ text-align:center; }
        .pmc-google-card p{ font-size:12.5px; color:var(--pmc-muted); margin:6px 0 10px; }
        .pmc-google-btn{ width:100%; border:1px solid var(--pmc-border); background:#fff; border-radius:8px; height:34px; font-size:12.5px; font-weight:500; color:var(--pmc-text); }
        .pmc-google-btn:hover{ background:var(--pmc-bg); }
        .pmc-connected-badge{ display:inline-flex; align-items:center; gap:6px; font-size:12px; color:#0f9d58; font-weight:600; }
        .pmc-main{ flex:1; min-width:0; display:flex; flex-direction:column; gap:14px; }
        .pmc-main-head{ display:flex; align-items:center; justify-content:space-between; }
        .pmc-main-head h5{ font-size:15px; font-weight:600; color:var(--pmc-text); margin:0; }
        .pmc-main-head span.count{ font-size:12.5px; color:var(--pmc-muted); margin-left:6px; font-weight:400; }
        .pmc-list{ display:flex; flex-direction:column; gap:12px; overflow-y:auto; padding-right:4px; flex:1; min-height:0; }
        .pmc-meeting-card{ background:#fff; border:1px solid var(--pmc-border); border-left:4px solid var(--pmc-accent, var(--pmc-primary)); border-radius:10px; padding:16px 18px; display:flex; flex-direction:column; gap:10px; transition:box-shadow .15s ease; }
        .pmc-meeting-card:hover{ box-shadow:0 4px 14px rgba(20,20,50,0.06); }
        .pmc-meeting-top{ display:flex; align-items:flex-start; justify-content:space-between; gap:10px; }
        .pmc-meeting-title{ font-size:14.5px; font-weight:600; color:var(--pmc-text); margin:0; }
        .pmc-meeting-time{ font-size:12.5px; color:var(--pmc-muted); display:flex; align-items:center; gap:6px; margin-top:3px; }
        .pmc-status-pill{ font-size:11.5px; font-weight:600; padding:3px 10px; border-radius:20px; text-transform:capitalize; white-space:nowrap; }
        .pmc-meeting-mid{ display:flex; align-items:center; gap:16px; flex-wrap:wrap; }
        .pmc-meeting-type{ display:flex; align-items:center; gap:6px; font-size:12.5px; color:var(--pmc-muted); }
        .pmc-meeting-type img{ width:16px; height:16px; }
        .pmc-meeting-desc{ font-size:12.5px; color:var(--pmc-muted); }
        .pmc-meeting-bottom{ display:flex; align-items:center; justify-content:space-between; }
        .pmc-avatars{ display:flex; align-items:center; }
        .pmc-avatar{ width:26px; height:26px; border-radius:50%; border:2px solid #fff; margin-left:-8px; background:var(--pmc-primary-soft); color:var(--pmc-primary); font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; }
        .pmc-avatar:first-child{ margin-left:0; }
        .pmc-more-count{ width:26px; height:26px; border-radius:50%; background:var(--pmc-bg); border:2px solid #fff; margin-left:-8px; font-size:10.5px; font-weight:700; color:var(--pmc-muted); display:flex; align-items:center; justify-content:center; }
        .pmc-card-actions button{ background:transparent; border:none; color:var(--pmc-muted); }
        .pmc-join-btn{ font-size:12px; font-weight:600; color:var(--pmc-primary); border:1px solid var(--pmc-primary-soft); background:var(--pmc-primary-soft); border-radius:6px; padding:5px 12px; }
        .pmc-join-btn:hover{ background:var(--pmc-primary); color:#fff; }
        .pmc-empty{ flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:12px; background:#fff; border:1px dashed var(--pmc-border); border-radius:12px; padding:40px; }
        .pmc-empty h6{ font-size:15px; font-weight:600; color:var(--pmc-text); margin:0; }
        .pmc-empty p{ font-size:13px; color:var(--pmc-muted); margin:0; text-align:center; max-width:320px; }
        .pmc-empty-icon{ width:56px; height:56px; border-radius:50%; background:var(--pmc-primary-soft); color:var(--pmc-primary); display:flex; align-items:center; justify-content:center; font-size:22px; }
        .pmc-loading{ flex:1; display:flex; align-items:center; justify-content:center; }
        .pmc-no-arrow::after{ display:none !important; }

        /* ---- modal ---- */
        .pmc-modal .modal-content{ border-radius:14px; border:none; overflow:hidden; }
        .pmc-modal-header{ display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #e2d4dc; }
        .pmc-modal-title{ font-size:16.5px; font-weight:600; color:var(--pmc-text); margin:0; }
        .pmc-modal-title span{ display:block; font-size:12px; font-weight:400; color:var(--pmc-muted); margin-top:2px; }
        .pmc-type-toggle{ display:inline-flex; background:var(--pmc-bg); border-radius:8px; padding:3px; gap:2px; }
        .pmc-type-toggle button{ border:none; background:transparent; font-size:12.5px; font-weight:600; padding:7px 16px; border-radius:6px; color:var(--pmc-muted); }
        .pmc-type-toggle button.active{ background:#fff; color:var(--pmc-primary); box-shadow:0 1px 3px rgba(20,20,50,.12); }
        .pmc-section-label{ font-size:11.5px; text-transform:uppercase; letter-spacing:.04em; font-weight:700; color:var(--pmc-muted); margin-bottom:10px; }
        .pmc-modal-footer{ display:flex; justify-content:flex-end; gap:10px; padding:16px 22px; border-top:1px solid var(--pmc-border); background:#fff; }
        .pmc-form-label{ font-size:12.5px; font-weight:600; color:var(--pmc-text); margin-bottom:5px; display:block; }
        .pmc-email-box{ border:1px solid var(--pmc-border); border-radius:8px; padding:6px 8px; min-height:42px; cursor:text; }
        .pmc-generate-link{ font-size:12.5px; font-weight:600; color:var(--pmc-primary); cursor:pointer; white-space:nowrap; }

        /* ---- list panel (left) ---- */
        .pmc-list-panel{  flex:none; display:flex; flex-direction:column; min-height:0; }
        .pmc-list-card{ display:flex; flex-direction:column; min-height:0; flex:1; padding:14px; }
        .pmc-list-card-head{ display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
        .pmc-list-card-head h6{ font-size:14px; font-weight:600; margin:0; color:var(--pmc-text); }
        .pmc-month-nav{ display:flex; align-items:center; gap:8px; font-size:12px; color:var(--pmc-muted); font-weight:600; }
        .pmc-month-nav button{ width:22px; height:22px; border-radius:6px; border:1px solid var(--pmc-border); background:#fff; color:var(--pmc-muted); display:flex; align-items:center; justify-content:center; font-size:10px; }
        .pmc-month-nav button:hover{ background:var(--pmc-primary-soft); color:var(--pmc-primary); }
        .pmc-day-strip{ display:flex; gap:5px; margin-bottom:10px; }
        .pmc-day-chip{ flex:1; border:1px solid var(--pmc-border); background:#fff; border-radius:9px; padding:6px 2px; display:flex; flex-direction:column; align-items:center; gap:1px; cursor:pointer; }
        .pmc-day-chip .dow{ font-size:9.5px; color:var(--pmc-muted); font-weight:600; text-transform:uppercase; }
        .pmc-day-chip .dom{ font-size:13px; font-weight:700; color:var(--pmc-text); }
        .pmc-day-chip:hover{ border-color:var(--pmc-primary); }
        .pmc-day-chip.selected{ background:var(--pmc-primary); border-color:var(--pmc-primary); }
        .pmc-day-chip.selected .dow, .pmc-day-chip.selected .dom{ color:#fff; }
        .pmc-history-head{ display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; }
        .pmc-history-head span.label{ font-size:11px; text-transform:uppercase; letter-spacing:.04em; color:var(--pmc-muted); font-weight:700; }
        .pmc-history-toggle{ display:flex; background:var(--pmc-bg); border-radius:7px; padding:2px; gap:2px; }
        .pmc-history-toggle span{ font-size:11px; font-weight:600; padding:4px 9px; border-radius:5px; color:var(--pmc-muted); cursor:pointer; }
        .pmc-history-toggle span.active{ background:#fff; color:var(--pmc-primary); box-shadow:0 1px 3px rgba(20,20,50,.12); }
        .pmc-history-list{ display:flex; flex-direction:column; gap:10px; flex:1; min-height:0; padding-right:2px;
    max-height: calc(-360px + 100vh);
    overflow-y: auto;
    scroll-behavior: smooth;
    width: 100%;
    scrollbar-width: thin;
    transition: scrollbar-color 0.3s; }
        .pmc-history-list::-webkit-scrollbar{ width:5px; }
        .pmc-history-list::-webkit-scrollbar-thumb{ background:#e2e8f0; border-radius:10px; }
        .pmc-history-item{ display:flex; align-items:flex-start; justify-content:space-between; gap:10px; padding:12px 14px; border-radius:12px; border:1px solid var(--pmc-border); background:#fff; cursor:pointer; box-shadow:0 1px 2px rgba(20,20,50,0.03); transition:box-shadow .15s ease, border-color .15s ease; }
        .pmc-history-item:hover{ border-color:var(--pmc-primary); box-shadow:0 4px 14px rgba(20,20,50,0.08); }
        .pmc-history-item.active{ background:var(--pmc-primary-soft); border-color:var(--pmc-primary); box-shadow:0 4px 14px rgba(122,58,94,0.12); }
        .pmc-history-item .content{ min-width:0; }
        .pmc-history-item .title{ font-size:14.5px; font-weight:700; margin-bottom:6px !important; color:var(--pmc-text); text-transform: capitalize; }
        .pmc-history-item .meta{ font-size:12.5px; color: #6b7280; margin-bottom:8px; font-weight:500; }
        .pmc-history-type-pill{ display:inline-flex; align-items:center; gap:5px; font-size:10.5px; font-weight:700; padding:3px 9px; border-radius:14px; border:none; white-space:nowrap; }
        .pmc-history-avatars{ display:flex; align-items:center; flex-shrink:0; margin-top:2px; }
        .pmc-history-avatar{ width:28px; height:28px; border-radius:50%; background:linear-gradient(45deg, var(--pmc-primary), #9d4d7a); color:#fff; font-size:10.5px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; border:2px solid #fff; box-shadow:0 1px 3px rgba(20,20,50,.15); }
        .pmc-history-avatar:not(:first-child){ margin-left:-9px; }
        .pmc-history-empty{ font-size:12.5px; color:var(--pmc-muted); text-align:center; padding:24px 8px; }

        /* ---- details panel (right) ---- */
        .pmc-detail-panel{ flex:1; min-width:0; display:flex; flex-direction:column; min-height:0; }
        .pmc-detail-card{ background:#fff; border:1px solid var(--pmc-border); border-radius:12px; flex:1; display:flex; flex-direction:column; min-height:0; overflow:hidden; position:relative; }
        .pmc-detail-header{ display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid var(--pmc-border); }
        .pmc-detail-header h6{ font-size:14px; font-weight:600; margin:0; color:var(--pmc-text); }
        .pmc-detail-body{ padding:20px; overflow-y:auto; flex:1; min-height:0; scrollbar-width:thin; transition:scrollbar-color 0.3s; }
        .pmc-detail-body::-webkit-scrollbar{ width:5px; }
        .pmc-detail-body::-webkit-scrollbar-thumb{ background:#e2e8f0; border-radius:10px; }
        .pmc-detail-meeting-title{font-weight:600;  text-transform:capitalize; color: rgb(26, 32, 44);
    text-transform: capitalize;
    font-size: 24px; }
        .pmc-detail-meta-row{ display:flex; align-items:center; gap:14px; color:var(--pmc-muted); font-size:14px; margin-bottom:10px; flex-wrap:wrap; }
        .pmc-detail-meta-row .item{ display:flex; align-items:center; gap:7px; font-weight: 500 !important; }
        .pmc-detail-badge-row{ display:flex; gap:8px; margin-bottom:16px; flex-wrap:wrap; }
        .pmc-badge-type{ display:flex; align-items:center; gap:6px; background:var(--pmc-primary); color:#fff; font-size:10px; font-weight:600; padding:6px 12px; border-radius:10px; }
        .pmc-badge-status{ display:flex; align-items:center; gap:5px; font-size:11px; font-weight:600; padding:6px 12px; border-radius:20px; text-transform:capitalize; }
        .pmc-detail-section-title{ display:flex; align-items:center; gap:8px; font-size:13px; font-weight:600; color:var(--pmc-text); margin:0 0 6px; }
        .pmc-detail-desc-box{ background:var(--pmc-bg); border-left:3px solid var(--pmc-primary); border-radius:8px; padding:11px 14px; font-size:13px; color:#4a5568; margin-bottom:16px; white-space:pre-wrap; }
        .pmc-detail-location{ display:flex; align-items:center; gap:10px; background:var(--pmc-bg); border:1px solid var(--pmc-border); border-radius:8px; padding:11px 14px; margin-bottom:8px; font-size:13px; font-weight:500; color:var(--pmc-text); }
        .pmc-detail-link-row{ display:flex; align-items:center; justify-content:space-between; gap:10px; padding:9px 12px; border-radius:8px; background:var(--pmc-primary-soft); border:1px dashed var(--pmc-primary); margin-bottom:4px; }
        .pmc-detail-link-row .link-text{ font-size:12.5px; font-weight:500; color:var(--pmc-primary); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .pmc-detail-link-row button{ background:#fff; border:1px solid var(--pmc-border); border-radius:6px; width:30px; height:30px; flex-shrink:0; }
        .pmc-detail-side-card{ background:#fff; border:1px solid var(--pmc-border); border-radius:12px; padding:16px; height:100%; }
        .pmc-detail-host-row{ display:flex; align-items:center; gap:12px; padding:8px; border-radius:8px; background:var(--pmc-bg); border:1px solid var(--pmc-border); }
        .pmc-detail-avatar-lg{ width:40px; height:40px; border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff; font-weight:700; font-size:13px; background:linear-gradient(45deg, var(--pmc-primary), #9d4d7a); flex-shrink:0; }
        .pmc-detail-invitee-row{ display:flex; align-items:center; gap:10px; margin-bottom:10px; }
        .pmc-detail-avatar-sm{ width:32px; height:32px; border-radius:50%; border:1px solid var(--pmc-border); background:var(--pmc-bg); color:var(--pmc-muted); font-weight:700; font-size:11px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .pmc-detail-footer{ position:absolute; left:0; right:0; bottom:0; padding:14px 20px; border-top:1px solid var(--pmc-border); background:#fff; }
        .pmc-detail-status-select .react-select__control{ min-height:32px; border-radius:7px; font-size:12px; }
        .pmc-detail-empty{ flex:1; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:10px; color:var(--pmc-muted); }
      `}</style>

      {/* -------- Top Bar -------- */}
      <div className="pmc-topbar">
        <div>
          <p className="pmc-title">Meetings</p>
          <p className="pmc-subtitle">
            Schedule, track and join meetings linked to this project
          </p>
        </div>
        <div className="pmc-topbar-actions">
          <div className="pmc-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              type="text"
              placeholder="Search meetings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="pmc-select-sm" style={{ minWidth: "160px" }}>
            <Select
              // classNamePrefix="react-select"
              value={statusFilterOptions.find((o) => o.value === statusFilter)}
              onChange={(opt) => setStatusFilter(opt.value)}
              options={statusFilterOptions}
              isSearchable={false}
              styles={customSelectStyles}
            />
          </div>
          <button
            className="pmc-btn-primary"
            onClick={openCreateModal}
            disabled={isReadOnly}
          >
            <FontAwesomeIcon icon={faPlus} /> Create Meeting
          </button>
        </div>
      </div>

      {/* -------- Body: Meeting History (left) + Meeting Details (right) -------- */}
      {!meetingGrant?.isView ? (
        <div className="pmc-body">
          <UnauthViewModal tabName={"Meetings"} />
        </div>
      ) : (
        <div className="pmc-body">
          {/* -------- Left: Meeting History list -------- */}
          <div className="pmc-list-panel col-md-4">
            <div className="pmc-card pmc-list-card">
              <div className="pmc-list-card-head">
                <h6>
                  {" "}
                  <FontAwesomeIcon icon={faCalendarDays} />
                  Meetings
                </h6>

                <div className="pmc-month-nav">
                  <button onClick={goToPrevWeek} aria-label="Previous week">
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <span>
                    {monthNames[weekDays[0].getMonth()]}{" "}
                    {weekDays[0].getFullYear()}
                  </span>
                  <button onClick={goToNextWeek} aria-label="Next week">
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>

              <div className="pmc-day-strip">
                {weekDays.map((d, idx) => {
                  const isSelected =
                    meetingFilter === "today" &&
                    isSameDay(d, selectedDateForApi);
                  return (
                    <div
                      key={idx}
                      className={`pmc-day-chip ${isSelected ? "selected" : ""}`}
                      onClick={() => selectDay(d)}
                    >
                      <span className="dow">{dayShortNames[d.getDay()]}</span>
                      <span className="dom">{d.getDate()}</span>
                    </div>
                  );
                })}
              </div>

              <div className="pmc-history-head">
                <span className="label">Meeting History</span>
                <div className="pmc-history-toggle">
                  <span
                    className={meetingFilter === "today" ? "active" : ""}
                    onClick={jumpToToday}
                  >
                    Day
                  </span>
                  <span
                    className={meetingFilter === "all" ? "active" : ""}
                    onClick={() => setMeetingFilter("all")}
                  >
                    All
                  </span>
                </div>
              </div>

              {meetingIsLoading ? (
                <div className="pmc-loading">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading meetings...</span>
                  </div>
                </div>
              ) : visibleMeetings?.length > 0 ? (
                <div className="pmc-history-list">
                  {visibleMeetings.map((meeting) => {
                    const meetingInvitees = meeting.invitees
                      ? JSON.parse(meeting.invitees)
                      : [];
                    const parsedMeetingDate = new Date(meeting.meeting_date);
                    const dateLabel =
                      !isNaN(parsedMeetingDate) &&
                        isSameDay(parsedMeetingDate, now)
                        ? "Today"
                        : meeting.meeting_date;
                    return (
                      <div
                        key={meeting.id}
                        className={`pmc-history-item meeting-card ${selectedMeetingId === meeting.id ? "active" : ""
                          }`}
                        onClick={() => setSelectedMeetingId(meeting.id)}
                      >
                        <div className="content text-truncate">
                          <p className="title text-truncate">
                            {truncate(meeting.meeting_title, 24)}
                          </p>
                          <div className="meta">
                            {dateLabel} • {meeting.meeting_from} -{" "}
                            {meeting.meeting_to}
                          </div>
                          <span
                            className="pmc-history-type-pill"
                            style={{
                              background: getMeetingTypeStyle(
                                meeting.meeting_type,
                              ).bg,
                              color: getMeetingTypeStyle(meeting.meeting_type)
                                .fg,
                            }}
                          >
                            <FontAwesomeIcon
                              icon={
                                meeting.meeting_type === "virtual"
                                  ? faVideo
                                  : faLocationDot
                              }
                            />
                            {meeting.meeting_type === "virtual"
                              ? "Google Meet"
                              : "In-Person"}
                          </span>
                        </div>
                        <div className="pmc-history-avatars">
                          {(meetingInvitees.length > 0
                            ? meetingInvitees.slice(0, 2)
                            : [{ fullName: meeting.added_name }]
                          ).map((p, idx) => (
                            <div
                              key={idx}
                              className="pmc-history-avatar"
                              title={p.fullName}
                            >
                              {initialsOf(p.fullName) || "?"}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="pmc-history-empty">
                  {meetingFilter === "today"
                    ? "No meetings on this day."
                    : "This project doesn't have any meetings yet."}
                  <div className="mt-2">
                    <button
                      className="pmc-btn-primary"
                      style={{ margin: "10px auto 0" }}
                      onClick={openCreateModal}
                      disabled={isReadOnly}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Create Meeting
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* -------- Right: Meeting Details -------- */}
          <div className="pmc-detail-panel">
            <div className="pmc-detail-card">
              {!selectedMeeting ? (
                <div className="pmc-detail-empty">
                  <FontAwesomeIcon icon={faCalendarDays} size="2x" />
                  <p className="mb-0">Select a meeting to view its details</p>
                </div>
              ) : (
                <>
                  <div className="pmc-detail-header">
                    <h6>Meeting Details</h6>
                    <div className="d-flex align-items-center gap-2">
                      {selectedMeeting.meeting_status === "scheduled" && (
                        <div
                          className="pmc-detail-status-select"
                          style={{ minWidth: "150px" }}
                        >
                          <Select
                            options={meetingStatusOptions}
                            placeholder="Update status..."
                            isSearchable={false}
                            isDisabled={isUpdatingStatus}
                            value={meetingStatusOptions.find(
                              (option) =>
                                option.value === selectedMeeting.meeting_status,
                            )}
                            onChange={(selectedOption) =>
                              handleQuickStatusChange(selectedOption.value)
                            }
                            styles={customSelectStyles}
                          // classNamePrefix="react-select"
                          />
                        </div>
                      )}
                      <Dropdown className="pmc-card-actions" align="end">
                        <Dropdown.Toggle
                          variant="light"
                          className="pmc-no-arrow p-0 m-0 bg-transparent border-0"
                        >
                          <FontAwesomeIcon icon={faEllipsisVertical} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="z-3">
                          <Dropdown.Item
                            onClick={() => {
                              if (!meetingGrant?.isEdit) {
                                toast.warning(
                                  "You are not authorized for this action",
                                );
                                return;
                              }
                              handleEditMeeting(
                                selectedMeeting.id,
                                selectedMeeting.module_id,
                              );
                            }}
                            disabled={isCancelled}
                          >
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              if (!meetingGrant?.isDelete) {
                                toast.warning(
                                  "You are not authorized for this action!",
                                );
                                return;
                              }
                              setDeleteMeetingId(selectedMeeting.id);
                              setConfirmDeleteMeeting(true);
                            }}
                            disabled={isCancelled}
                          >
                            Delete
                          </Dropdown.Item>
                          {selectedMeeting.meeting_status.toLowerCase() !==
                            "cancelled" && (
                              <Dropdown.Item
                                onClick={() => {
                                  if (!meetingGrant?.isEdit) {
                                    toast.warning(
                                      "You are not authorized for this action!",
                                    );
                                    return;
                                  }
                                  handleCancelMeeting(selectedMeeting.id);
                                }}
                                disabled={isCancelled}
                              >
                                {isCancelled ? "Cancelling..." : "Cancel Meeting"}
                              </Dropdown.Item>
                            )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>

                  <div
                    className="pmc-detail-body"
                    style={
                      showJoinButton ? { paddingBottom: "84px" } : undefined
                    }
                  >
                    <Row className="g-4">
                      {/* Left column: core info */}
                      <Col lg={7}>
                        <h3 className="pmc-detail-meeting-title">
                          {selectedMeeting.meeting_title}
                        </h3>

                        <div className="pmc-detail-meta-row mb-3">
                          <div className="item fw-medium fs-16">
                            <FontAwesomeIcon
                              icon={faCalendarDays}
                              style={{ color: "var(--pmc-primary)" }}
                            />
                            {formatDate(selectedMeeting.meeting_date)}
                          </div>
                          <div className="item">
                            <FontAwesomeIcon
                              icon={faClock}
                              style={{ color: "var(--pmc-primary)" }}
                            />
                            {formatTime(selectedMeeting.meeting_from)} -{" "}
                            {formatTime(selectedMeeting.meeting_to)}
                          </div>
                        </div>

                        <div className="pmc-detail-badge-row mb-3">
                          <span
                            className="pmc-badge-type"
                            style={{
                              background: getMeetingTypeStyle(
                                selectedMeeting.meeting_type,
                              ).bg,
                              color: getMeetingTypeStyle(
                                selectedMeeting.meeting_type,
                              ).fg,
                            }}
                          >
                            <FontAwesomeIcon
                              icon={
                                selectedMeeting.meeting_type === "virtual"
                                  ? faVideo
                                  : faLocationDot
                              }
                            />
                            {selectedMeeting.meeting_type}
                          </span>
                          <span
                            className="pmc-badge-status"
                            style={{
                              background: getMeetingStatusStyle(
                                selectedMeeting.meeting_status,
                              ).bg,
                              color: getMeetingStatusStyle(
                                selectedMeeting.meeting_status,
                              ).fg,
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faCircle}
                              style={{ fontSize: "7px" }}
                            />
                            {selectedMeeting.meeting_status}
                          </span>
                        </div>

                        <p className="pmc-detail-section-title mb-3">
                          <FontAwesomeIcon
                            icon={faCircleInfo}
                            className="text-muted"
                          />{" "}
                          Description
                        </p>
                        <div className="pmc-detail-desc-box">
                          {selectedMeeting.description || "No description."}
                        </div>

                        <p className="pmc-detail-section-title mb-3">
                          Access &amp; Link
                        </p>
                        <div className="pmc-detail-location mb-3">
                          <FontAwesomeIcon
                            icon={faLocationDot}
                            style={{ color: "var(--pmc-primary)" }}
                          />
                          {selectedMeeting.address || "-"}
                        </div>
                        <div className="pmc-detail-link-row">
                          <div className="d-flex align-items-center gap-2 text-truncate">
                            <FontAwesomeIcon
                              icon={faLink}
                              style={{ color: "var(--pmc-primary)" }}
                            />
                            <small className="link-text">
                              {selectedMeeting.meeting_link ||
                                "No meeting link."}
                            </small>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              selectedMeeting.meeting_link &&
                              navigator.clipboard.writeText(
                                selectedMeeting.meeting_link,
                              )
                            }
                          >
                            <FontAwesomeIcon
                              icon={faCopy}
                              style={{ color: "#64748b" }}
                            />
                          </button>
                        </div>
                      </Col>

                      {/* Right column: people */}
                      <Col lg={5}>
                        <div className="pmc-detail-side-card">
                          <p className="pmc-detail-section-title">Host</p>
                          <div className="pmc-detail-host-row mb-4">
                            <div className="pmc-detail-avatar-lg">
                              {initialsOf(selectedMeeting.added_name)}
                            </div>
                            <div>
                              <div
                                className="fw-bold"
                                style={{ fontSize: "14px", color: "#1a202c" }}
                              >
                                {selectedMeeting.added_name}
                              </div>
                              <div
                                className="text-uppercase fw-bold"
                                style={{
                                  fontSize: "9px",
                                  color: "var(--pmc-primary)",
                                  letterSpacing: "0.5px",
                                }}
                              >
                                Organizer
                              </div>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <p className="pmc-detail-section-title mb-0">
                              <FontAwesomeIcon
                                icon={faUsers}
                                className="text-muted"
                              />{" "}
                              Invitees
                            </p>
                            <span
                              className="pmc-status-pill"
                              style={{
                                background: "#f1f3f4",
                                color: "#636e72",
                              }}
                            >
                              {selectedInvitees?.length || 0}
                            </span>
                          </div>
                          <div
                            style={{ maxHeight: "180px", overflowY: "auto" }}
                          >
                            {selectedInvitees?.map((person, idx) => (
                              <div key={idx} className="pmc-detail-invitee-row">
                                <div className="pmc-detail-avatar-sm">
                                  {initialsOf(person.fullName)}
                                </div>
                                <div className="text-truncate">
                                  <div
                                    className="fw-semibold text-dark text-truncate"
                                    style={{ fontSize: "13px" }}
                                  >
                                    {person.fullName}
                                  </div>
                                  <div
                                    className="text-muted text-truncate"
                                    style={{ fontSize: "11px" }}
                                  >
                                    {person.email}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="mt-3 pt-3 border-top">
                            <p className="pmc-detail-section-title">
                              Assigned Team
                            </p>
                            <div
                              className="fw-bold d-flex align-items-center"
                              style={{
                                color: "var(--pmc-primary)",
                                fontSize: "13px",
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faUsers}
                                className="me-2"
                              />
                              {selectedMeeting.assigned_name}
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  {showJoinButton && (
                    <div className="pmc-detail-footer">
                      <a
                        href={selectedMeeting.meeting_link}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-lg w-100 fw-bold text-white"
                        style={{
                          backgroundColor: "var(--pmc-primary)",
                          borderRadius: "10px",
                          fontSize: "15px",
                          border: "none",
                          padding: "12px",
                        }}
                      >
                        Join Meeting Now
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmDeleteMeeting && (
        <DeleteModal
          show={confirmDeleteMeeting}
          onClose={setConfirmDeleteMeeting}
          onConfirm={handleDeleteMeeting}
          isLoading={loading}
          message="Are you sure you want to delete this item?"
        />
      )}

      {/* -------- Create / Edit Modal -------- */}
      <Modal
        show={showAddForm || editMeetingForm}
        onHide={handleCloseModal}
        centered
        size="xl"
        backdrop="static"
        className="pmc-modal"
      >
        <div className="pmc-modal-header">
          <div>
            <p className="pmc-modal-title">
              {editMeetingForm ? "Edit Meeting" : "Schedule New Meeting"}
              <span>Fill in the details and invite your attendees</span>
            </p>
          </div>
          <div className="d-flex align-items-center gap-2">
            <div className="d-flex gap-2">
              {/* Neither is logged in: Show both Connect buttons */}
              {!isGoogleLoggedIn && !isOutlookLoggedIn && (
                <>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => googleLogin()}
                  >
                    🔗 Connect Google
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={handleOutlookLogin}
                  >
                    🔗 Connect Outlook
                  </button>
                </>
              )}

              {/* Only Google is logged in: Show ONLY Google Logout */}
              {isGoogleLoggedIn && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={handleGoogleLogout}
                >
                  🔓 Logout Google
                </button>
              )}

              {/* Only Outlook is logged in: Show ONLY Outlook Logout */}
              {isOutlookLoggedIn && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={handleOutlookLogout}
                >
                  🔓 Logout Outlook
                </button>
              )}
            </div>
            <button
              type="button"
              className="btn btn-light border-0"
              onClick={handleCloseModal}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        <div style={{ position: "relative" }}>
          {isPosting && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
            >
              <div className="d-flex flex-column align-items-center">
                <div
                  className="spinner-border text-primary"
                  role="status"
                  style={{ width: "3rem", height: "3rem" }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-2 text-primary fw-semibold">
                  {editMeetingForm
                    ? "Updating meeting..."
                    : "Creating meeting..."}
                </div>
              </div>
            </div>
          )}

          <Modal.Body
            style={{ maxHeight: "68vh", overflowY: "auto", padding: "22px" }}
          >
            <Form
              className="form-container"
              id="pmc-meeting-form"
              onSubmit={
                editMeetingForm
                  ? handleSubmit(handleUpdateMeeting)
                  : handleSubmit(onSubmit)
              }
            >
              <Row>
                {/* ---------------- LEFT COLUMN ---------------- */}
                <Col md={7} className="pe-md-4">
                  <p className="pmc-section-label">Meeting Details</p>

                  <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative ">
                    <label className="pmc-form-label">
                      Meeting Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Weekly project sync"
                      {...register("meeting_title")}
                    />
                    {errors.meeting_title && (
                      <div
                        className="text-danger mt-1"
                        style={{ fontSize: "12px" }}
                      >
                        {errors.meeting_title.message}
                      </div>
                    )}
                  </Form.Group>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative ">
                        <label className="pmc-form-label">
                          Date <span className="text-danger">*</span>
                        </label>
                        <Form.Control
                          type="date"
                          min={today}
                          {...register("meeting_date")}
                        />
                        {errors.meeting_date && (
                          <div
                            className="text-danger mt-1"
                            style={{ fontSize: "12px" }}
                          >
                            {errors.meeting_date.message}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative ">
                        <label className="pmc-form-label">
                          From <span className="text-danger">*</span>
                        </label>
                        <input
                          type="time"
                          className="form-control"
                          {...register("meeting_from")}
                        />
                        {errors.meeting_from && (
                          <div
                            className="text-danger mt-1"
                            style={{ fontSize: "12px" }}
                          >
                            {errors.meeting_from.message}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative ">
                        <label className="pmc-form-label">
                          To <span className="text-danger">*</span>
                        </label>
                        <input
                          type="time"
                          className="form-control"
                          {...register("meeting_to")}
                        />
                        {errors.meeting_to && (
                          <div
                            className="text-danger mt-1"
                            style={{ fontSize: "12px" }}
                          >
                            {errors.meeting_to.message}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-2 form-z-index position-relative reactSelectedDiv">
                    <label>Meeting Type </label>
                    <Controller
                      name="meeting_type"
                      control={control}
                      style={{ minHeight: "38px", paddingTop: "0px" }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={meetingTypeOptions}
                          placeholder="Select meeting type..."
                          classNamePrefix="react-select"
                          styles={customStyles}
                          value={meetingTypeOptions.find(
                            (option) => option.value === field.value,
                          )}
                          onChange={(selectedOption) =>
                            field.onChange(selectedOption.value)
                          }
                        />
                      )}
                    />
                    {errors.meeting_type && (
                      <div
                        className="text-danger mt-1"
                        style={{ fontSize: "12px", marginLeft: "2px" }}
                      >
                        {errors.meeting_type.message}
                      </div>
                    )}
                  </Form.Group>
                  {meetingType === "virtual" ? (
                    <Form.Group className="col-12 position-relative form-z-index">
                      <Form.Label
                        className="w-auto"
                        style={{ marginBottom: 1 }}
                      >
                        Meeting Link <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="d-flex gap-2 align-items-center">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Paste your Zoom/Meet URL, or click Generate "
                          {...register("meeting_link")}
                        />
                      </div>
                      <div className="d-flex justify-content-between">
                        <div>
                          {errors.meeting_link && (
                            <div
                              className="text-danger mt-1"
                              style={{ fontSize: "12px", marginLeft: "2px" }}
                            >
                              {errors.meeting_link.message}
                            </div>
                          )}
                        </div>
                        <div className="d-flex align-items-center">
                          {isGoogleLoggedIn && meetingType === "virtual" && (
                            <button
                              type="button"
                              className="cursor-pointer fw-400 fs-13 d-flex align-items-center"
                              style={{
                                color: "#0078d4",
                                background: "transparent",
                                border: "none",
                                padding: 0,
                                margin: 0,
                                fontSize: "13px",
                                cursor: "pointer",
                              }}
                              disabled={
                                !!watch("meeting_link") || isGeneratingMeetLink
                              }
                              onClick={async () => {
                                try {
                                  setIsGeneratingMeetLink(true);
                                  const meetDate = watch("meeting_date");
                                  const meetFrom = watch("meeting_from");
                                  const meetTo = watch("meeting_to");
                                  const title = watch("meeting_title");
                                  if (
                                    !meetDate ||
                                    !meetFrom ||
                                    !meetTo ||
                                    !title
                                  ) {
                                    return toast.error(
                                      `Please provide meeting title`,
                                    );
                                  }
                                  const startISO = new Date(
                                    `${meetDate}T${meetFrom}`,
                                  ).toISOString();
                                  const endISO = new Date(
                                    `${meetDate}T${meetTo}`,
                                  ).toISOString();
                                  const meetResult =
                                    await createGoogleMeetEvent(
                                      title,
                                      startISO,
                                      endISO,
                                    );
                                  const meetLink =
                                    meetResult?.meetLink || meetResult;
                                  const eventId = meetResult?.eventId;
                                  if (meetLink) {
                                    setValue("meeting_link", meetLink);
                                    if (eventId) setDraftMeetEventId(eventId);
                                    toast.success(
                                      "Google Meet link generated!",
                                    );
                                  } else {
                                    toast.error(
                                      "Could not get Meet link. Try again.",
                                    );
                                  }
                                } catch (err) {
                                  toast.error("Failed to generate Meet link");
                                } finally {
                                  setIsGeneratingMeetLink(false);
                                }
                              }}
                            >
                              {isGeneratingMeetLink
                                ? "Generating..."
                                : " Generate Meet Link"}
                            </button>
                          )}
                          {/* Outlook Teams Link Generator */}
                          {isOutlookLoggedIn && (
                            <button
                              type="button"
                              disabled={
                                !!watch("meeting_link") ||
                                isGeneratingOutlookLink
                              }
                              className="cursor-pointer fw-400 fs-13 d-flex align-items-center"
                              style={{
                                color: "#0078d4",
                                background: "transparent",
                                border: "none",
                                padding: 0,
                                margin: 0,
                                fontSize: "13px",
                                cursor: "pointer",
                              }}
                              onClick={handleGenerateTeamsLink}
                            >
                              {isGeneratingOutlookLink
                                ? "Generating..."
                                : "Generate Teams Link"}
                            </button>
                          )}
                          {teamsMeetingType === "calender_meeting" && (
                            <div
                              className="position-relative"
                              onMouseEnter={() => setInfoHovered(true)}
                              onMouseLeave={() => setInfoHovered(false)}
                            >
                              <button
                                type="button"
                                className="btn p-1 text-muted shadow-none line-height-1"
                                style={{ transition: "color 0.15s ease" }}
                              >
                                <FontAwesomeIcon
                                  icon={faQuestionCircle}
                                  style={{ fontSize: "1.2rem" }}
                                />
                              </button>
                            </div>
                          )}
 
                          {isInfoHovered && (
                            <div
                              className="position-absolute"
                              style={{
                                top: "110%",
                                left: 0,
                                width: "650px",
                                maxWidth: "650px",
                                background: "#fff",
                                border: "1px solid #e0e4f8",
                                borderRadius: "8px",
                                padding: "12px",
                                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                zIndex: 1000,
                              }}
                            >
                              <h6
                                style={{
                                  marginBottom: "10px",
                                  color: "#7a3a5e",
                                  fontWeight: 600,
                                  fontSize: "13px",
                                }}
                              >
                                Teams Meeting Link Guide
                              </h6>
 
                              {meetingLinkDetails.map((c, index) => (
                                <div
                                  key={c.label}
                                  style={{
                                    marginBottom:
                                      index === meetingLinkDetails.length - 1
                                        ? 0
                                        : "10px",
                                    paddingBottom:
                                      index === meetingLinkDetails.length - 1
                                        ? 0
                                        : "10px",
                                    borderBottom:
                                      index === meetingLinkDetails.length - 1
                                        ? "none"
                                        : "1px solid #f1f3f8",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: 600,
                                      color: "#2e384d",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    {c.label}
                                  </div>
 
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      color: "#6b7280",
                                      lineHeight: 1.5,
                                    }}
                                  >
                                    {c.description}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </Form.Group>
                  ) : (
                    <div className="col-12 ">
                      <Form.Group className="col-12 position-relative form-z-index">
                        <Form.Label
                          className="w-auto"
                          style={{ marginBottom: 1 }}
                        >
                          Location <span className="text-danger">*</span>
                        </Form.Label>
                        <input
                          type="text"
                          className={`form-control`}
                          placeholder="Address..."
                          {...register("address")}
                        />
                        {errors.address && (
                          <div
                            className="text-danger mt-1"
                            style={{ fontSize: "12px", marginLeft: "2px" }}
                          >
                            {errors.address.message}
                          </div>
                        )}
                      </Form.Group>
                    </div>
                  )}

                  <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative mt-3 ">
                    <label className="pmc-form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows={4}
                      placeholder="Agenda, notes, or context for attendees..."
                      {...register("description")}
                    />
                  </Form.Group>
                </Col>

                {/* ---------------- RIGHT COLUMN ---------------- */}
                <Col
                  md={5}
                  className="ps-md-4"
                  style={{ borderLeft: "1px solid var(--pmc-border)" }}
                >
                  <p className="pmc-section-label">Assignment &amp; Invitees</p>
                  <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative ">
                    <label className="pmc-form-label">Assign To</label>
                    <Controller
                      name="assign_to"
                      control={control}
                      render={({ field }) => (
                        <Select
                          styles={customStyles}
                          {...field}
                          options={leadOwner}
                          value={
                            leadOwner.find(
                              (lead) => lead.value === field.value,
                            )
                              ? {
                                value: field.value,
                                label: leadOwner.find(
                                  (lead) => lead.value === field.value,
                                )?.label,
                              }
                              : leadOwner[0]
                                ? {
                                  value: leadOwner[0].value,
                                  label: leadOwner[0].label,
                                }
                                : null
                          }
                          onChange={(selectedOption) =>
                            field.onChange(
                              selectedOption?.value ||
                              loginUserId,
                            )
                          }
                          isClearable
                          classNamePrefix="react-select"
                        />
                      )}
                    />
                    {hasConflict && (
                      <div
                        className="text-warning mt-1"
                        style={{ fontSize: "12px" }}
                      >
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="me-1"
                        />
                        {warningMessage !== "" && warningMessage}
                      </div>
                    )}
                    {errors.assign_to && (
                      <div
                        className="text-danger mt-1"
                        style={{ fontSize: "12px" }}
                      >
                        {errors.assign_to.message}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative ">
                    <label className="pmc-form-label">Meeting Status</label>
                    <Controller
                      name="meeting_status"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={meetingStatusOptions}
                          styles={customStyles}
                          classNamePrefix="react-select"
                          placeholder="Select status..."
                          value={meetingStatusOptions.find(
                            (option) => option.value === field.value,
                          )}
                          onChange={(selectedOption) =>
                            field.onChange(selectedOption.value)
                          }
                        />
                      )}
                    />
                    {errors.meeting_status && (
                      <div
                        className="text-danger mt-1"
                        style={{ fontSize: "12px" }}
                      >
                        {errors.meeting_status.message}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3 d-flex flex-column focus-z-index position-relative ">
                    <label className="pmc-form-label">
                      Timezone <span className="text-danger">*</span>
                    </label>
                    <TimezoneSelect
                      styles={customStyles}
                      timezones={{
                        ...allTimezones,
                        "America/Lima": "Pittsburgh",
                        "Europe/Berlin": "Frankfurt",
                      }}
                      value={selectedTimezone}
                      onChange={setSelectedTimezone}
                    />
                  </Form.Group>
                  <Form.Group className=" position-relative form-z-index mb-3">
                    <div className="">
                      {/* <label className="form-label">
                      Invitees <span className="text-danger">*</span>
                    </label> */}
                      <Form.Label
                        className="w-auto"
                        style={{ marginBottom: 1 }}
                      >
                        Invitees <span className="text-danger">*</span>
                      </Form.Label>
                      <div
                        className="border rounded p-2"
                        style={{ minHeight: "40px", cursor: "text" }}
                        onClick={() =>
                          document.getElementById("email-input").focus()
                        }
                      >
                        <div
                          onClick={() => setShowEmailDropdown(true)}
                          className="d-flex flex-wrap gap-1 align-items-center"
                        >
                          {emailRecipients.map((e, index) => (
                            <span
                              key={index}
                              className="badge bg-primary d-flex align-items-center gap-1 mt-3"
                              style={{ fontSize: "12px", padding: "4px 8px" }}
                            >
                              {e}
                              {e !== email && (
                                <FontAwesomeIcon
                                  icon={faTimes}
                                  style={{
                                    cursor: "pointer",
                                    fontSize: "10px",
                                  }}
                                  onClick={() => removeEmailTag(e)}
                                />
                              )}
                            </span>
                          ))}
                          <input
                            id="email-input"
                            type="text"
                            className="border-0 flex-grow-1"
                            style={{
                              outline: "none",
                              minWidth: "200px",
                              background: "transparent",
                            }}
                            placeholder={
                              emailRecipients.length === 0
                                ? "Press ENTER to add email..."
                                : "Add more emails..."
                            }
                            value={currentEmailInput}
                            onChange={handleEmailInputChange}
                            onKeyDown={handleEmailInputKeyDown}
                            onFocus={() => currentEmailInput.length > 0}
                          />
                        </div>
                      </div>

                      {showEmailDropdown && (
                        <div
                          className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                          style={{
                            zIndex: 1000,
                            maxHeight: "300px",
                            overflowY: "auto",
                          }}
                          ref={emailDropdownRef}
                        >
                          {isLoadingEmails ? (
                            <div className="p-3 text-center text-muted">
                              <small>Loading emails...</small>
                            </div>
                          ) : (
                            <>
                              {Object.entries(emailOptions).map(
                                ([categoryName, contacts]) =>
                                  contacts.length > 0 && (
                                    <div key={categoryName}>
                                      <div className="px-3 py-2 bg-light border-bottom">
                                        <small className="fw-bold text-muted">
                                          {categoryName.toUpperCase()} (
                                          {contacts.length})
                                        </small>
                                      </div>
                                      {contacts.map((contact, index) => (
                                        <div
                                          key={`${categoryName}-${contact.id || index}`}
                                          className="px-3 py-2 hover-bg-light cursor-pointer border-bottom"
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            addEmailTag(contact?.email)
                                          }
                                          onMouseEnter={(e) =>
                                          (e.target.style.backgroundColor =
                                            "#f8f9fa")
                                          }
                                          onMouseLeave={(e) =>
                                          (e.target.style.backgroundColor =
                                            "transparent")
                                          }
                                        >
                                          <div className="d-flex align-items-center">
                                            <div>
                                              <div
                                                style={{
                                                  fontSize: "14px",
                                                  fontWeight: "500",
                                                }}
                                              >
                                                {contact.fullName}
                                              </div>
                                              <small className="text-muted">
                                                {contact.email}
                                              </small>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ),
                              )}

                              {Object.values(emailOptions).every(
                                (contacts) => contacts.length === 0,
                              ) &&
                                !isLoadingEmails && (
                                  <div className="p-3 text-center text-muted">
                                    <small>
                                      No emails found. Type to search or press
                                      Enter to add manually.
                                    </small>
                                  </div>
                                )}
                            </>
                          )}
                        </div>
                      )}
                      {emailRecipients.length === 0 && (
                        <div className="invalid-feedback d-block">
                          At least one invitee is required
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>

          <div className="pmc-modal-footer">
            <Button
              type="button"
              className="btn btn-default-outline"
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="pmc-meeting-form"
              className="btn btn-default"
              style={{ border: "none" }}
              disabled={isPosting}
            >
              {editMeetingForm ? "Update Meeting" : "Schedule Meeting"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectMeeting;
